import { useCallback, useMemo, useRef, useState } from "react";
import {
  buildDefaultEnabledMap,
  buildEnabledMapFromSentAssets,
  getActiveAdditionalAssets,
  normalizeAdditionalAssets,
} from "../utils/additionalAssetsUtils";

export const useAdditionalAssetsSelection = () => {
  const [assets, setAssets] = useState([]);
  const [enabledMap, setEnabledMap] = useState({});
  const pendingSentAssetsRef = useRef(null);

  const onLoadAssets = useCallback(
    ({ assets: rawAssets, sentAssets = null }) => {
      const normalizedAssets = normalizeAdditionalAssets(rawAssets);
      const resolvedSentAssets = sentAssets ?? pendingSentAssetsRef.current;

      setAssets(normalizedAssets);

      if (Array.isArray(resolvedSentAssets)) {
        setEnabledMap(
          buildEnabledMapFromSentAssets({
            assets: normalizedAssets,
            sentAssets: resolvedSentAssets,
          })
        );
        pendingSentAssetsRef.current = null;
        return;
      }

      setEnabledMap((prev) => {
        const defaultMap = buildDefaultEnabledMap({ assets: normalizedAssets });

        if (Object.keys(prev).length === 0) {
          return defaultMap;
        }

        return normalizedAssets.reduce((map, _, index) => {
          map[index] =
            typeof prev[index] === "boolean" ? prev[index] : defaultMap[index];
          return map;
        }, {});
      });
    },
    []
  );

  const onToggleAsset = useCallback(({ index, enabled }) => {
    setEnabledMap((prev) => ({
      ...prev,
      [index]: enabled,
    }));
  }, []);

  const onRestoreEnabledMapFromSentAssets = useCallback(
    ({ sentAssets = [] }) => {
      if (assets.length === 0) {
        pendingSentAssetsRef.current = sentAssets;
        return;
      }

      setEnabledMap(buildEnabledMapFromSentAssets({ assets, sentAssets }));
    },
    [assets]
  );

  const activeAssets = useMemo(
    () => getActiveAdditionalAssets({ assets, enabledMap }),
    [assets, enabledMap]
  );

  return {
    assets,
    enabledMap,
    activeAssets,
    onLoadAssets,
    onToggleAsset,
    onRestoreEnabledMapFromSentAssets,
  };
};
